<template>
  <Section
    :id="id"
    class="quick-quote-line-items"
    :header="t('lineItems.lineItems', 2)"
  >
    <template #default>
      <QuickQuoteLineItemSummaryCard :line-items="internalValue" />

      <ul role="list" class="quick-quote-line-items-list">
        <li v-for="(lineItem, index) in internalValue" :key="index">
          <LineItemEdit
            :key="index"
            :model-value="lineItem"
            :index="index"
            :removable="internalValue.length > 1"
            :handling-units="handlingUnits"
            disable-default-reference-numbers
            expanded
            @duplicate="onDuplicate(index)"
            @remove="onRemove(index)"
            @update:model-value="onUpdate(index, $event)"
          />
        </li>
      </ul>
    </template>

    <template #footer>
      <AddButton
        :label="t('lineItems.lineItems')"
        data-testid="quick-quote-add-line-item-button"
        @click="onAdd"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { onMounted, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useValidations } from '@rygen/rygen-ui/composables';
import { notNull } from '@rygen/rygen-ui/util';

import { LineItem, QuickQuoteLineItem } from '@/model';
import { useHandlingUnitStore } from '@/stores/HandlingUnit';
import { useParentEntityStore } from '@/stores/ParentEntity';

import LineItemEdit from '@/components/lineItem/LineItemEdit.vue';
import QuickQuoteLineItemSummaryCard from '@/components/quickQuote/QuickQuoteLineItemSummaryCard.vue';
import AddButton from '@/components/ui/AddButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array as PropType<QuickQuoteLineItem[]>,
    default: () => [],
  },
});
const { id, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', modelValue: QuickQuoteLineItem[]): void;
}>();

const { handlingUnits, loadHandlingUnits } = useHandlingUnitStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod(opts: Partial<QuickQuoteLineItem[]>) {
    return opts?.filter(notNull) ?? [];
  },
});
const { owningClient } = useParentEntityStore();

const onAdd = async () => {
  internalValue.value.push(new QuickQuoteLineItem());
};

const onDuplicate = (index: number) => {
  const lineItem = internalValue.value[index];
  const newLineItem = new QuickQuoteLineItem(lineItem);

  internalValue.value.push(newLineItem);
};

const onRemove = (index: number) => {
  internalValue.value.splice(index, 1);
};

const onUpdate = (index: number, lineItem: LineItem) => {
  internalValue.value.splice(index, 1, lineItem as QuickQuoteLineItem);
};

onMounted(() => {
  if (owningClient.value?.id) {
    loadHandlingUnits();
  }
});

const { validate } = useValidations(internalValue, {});

defineExpose({ validate });
</script>

<style>
.quick-quote-line-items {
  .section-data {
    display: flex;
    flex-direction: column;

    .quick-quote-line-item-summary-card {
      margin-bottom: var(--sp-4);
    }

    .quick-quote-line-items-list {
      counter-reset: line-item-num;
      display: flex;
      flex-direction: column;
      gap: var(--gap-1);
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }

  .section-footer {
    display: flex;
    align-items: center;
    gap: var(--gap-1);
    margin-top: var(--gap-1);
  }
}
</style>
